/**
 * Design Library — block patterns (small, reusable units of meaning).
 * Pure design knowledge: no pixels, geometry, or editor concepts.
 */

import { definePattern } from './_define';

export const BLOCK_PATTERNS = [
  definePattern({
    id: 'premium-cta', name: 'Premium CTA', family: 'block',
    purpose: 'A single, unmistakable call to action.',
    communicationGoal: 'Tell the viewer exactly what to do next.',
    visualGoal: 'High-contrast, comfortable, confident.',
    contentGoal: 'An action verb and, optionally, a reassurance line.',
    marketingGoal: 'Convert attention into action.',
    idealDocuments: ['Flyer', 'Poster', 'Instagram Post', 'Email Header', 'Presentation', 'Banner'],
    idealIndustries: ['retail', 'fitness', 'technology', 'medical', 'travel', 'corporate'],
    idealAudiences: ['general-public', 'professionals', 'families'],
    idealGenomes: ['modern', 'bold', 'corporate', 'glassmorphism', 'luxury'],
    compatiblePatterns: ['split-hero', 'offer-section', 'pricing-section', 'gradient-hero'],
    compositionPhilosophy: 'Isolated in whitespace so nothing competes with it.',
    hierarchyPhilosophy: 'The final and loudest step in the reading flow.',
    typicalSections: ['cta-label', 'reassurance'],
    preferredComponents: ['cta'],
    premiumCues: ['confident-scale', 'considered-color'],
    thingsToAvoid: ['tiny-cta', 'multiple-primary-ctas', 'low-contrast'],
    variationRules: ['shape-language', 'label-length', 'placement'],
    capabilities: ['cta'],
    tags: ['cta', 'conversion', 'block'],
    expansionRules: { emphasis: 'action', isolation: 'whitespace' },
  }),
  definePattern({
    id: 'discount-block', name: 'Discount Block', family: 'block',
    purpose: 'A loud badge carrying a percentage or saving.',
    communicationGoal: 'Broadcast the saving in one beat.',
    visualGoal: 'A shape that deliberately interrupts the composition.',
    contentGoal: 'The number, the qualifier, the expiry.',
    marketingGoal: 'Create urgency.',
    idealDocuments: ['Flyer', 'Poster', 'Instagram Post', 'Instagram Story', 'Banner'],
    idealIndustries: ['retail', 'restaurant', 'salon', 'fitness', 'food'],
    idealAudiences: ['general-public', 'students', 'families'],
    idealGenomes: ['bold', 'playful', 'retro', 'memphis'],
    compatiblePatterns: ['offer-section', 'flyer-hero', 'instagram-story-hero'],
    incompatiblePatterns: ['minimal-stack'],
    compositionPhilosophy: 'An intentional interruption; the badge breaks the grid.',
    hierarchyPhilosophy: 'Second only to the offer itself.',
    typicalSections: ['discount-value', 'qualifier', 'expiry'],
    preferredComponents: ['cta'],
    premiumCues: ['confident-scale'],
    thingsToAvoid: ['clutter', 'illegible-badge'],
    variationRules: ['badge-shape', 'rotation', 'placement'],
    capabilities: ['offer'],
    tags: ['discount', 'badge', 'block'],
    expansionRules: { emphasis: 'value', breakGrid: 'intentional' },
  }),
  definePattern({
    id: 'qr-block', name: 'QR Block', family: 'block',
    purpose: 'A scannable bridge from print to digital.',
    communicationGoal: 'Move the viewer from paper to screen.',
    visualGoal: 'A quiet, generous, scannable square.',
    contentGoal: 'The code plus a short instruction.',
    marketingGoal: 'Make the offline design measurable.',
    idealDocuments: ['Flyer', 'Poster', 'Business Card', 'Menu', 'Brochure', 'Pamphlet'],
    idealIndustries: ['restaurant', 'retail', 'events', 'real-estate', 'medical'],
    idealAudiences: ['general-public', 'tourists'],
    idealGenomes: ['modern', 'minimal', 'corporate', 'scandinavian'],
    compatiblePatterns: ['contact-section', 'restaurant-menu', 'corporate-footer'],
    compositionPhilosophy: 'Given quiet margin; never crowded or shrunk.',
    hierarchyPhilosophy: 'Supporting — but always large enough to scan.',
    typicalSections: ['qr', 'instruction'],
    preferredComponents: ['contact'],
    premiumCues: ['precision'],
    thingsToAvoid: ['tiny-qr', 'busy-background-behind-qr'],
    variationRules: ['instruction-copy', 'placement'],
    capabilities: ['qr'],
    tags: ['qr', 'bridge', 'block'],
    expansionRules: { emphasis: 'scannability', margin: 'generous' },
  }),
  definePattern({
    id: 'logo-strip', name: 'Logo Strip', family: 'block',
    purpose: 'A row of marks that borrow credibility.',
    communicationGoal: 'Show the company you keep.',
    visualGoal: 'Even, muted, uniform marks.',
    contentGoal: 'Three to six logos, optically balanced.',
    marketingGoal: 'Transfer trust by association.',
    idealDocuments: ['Presentation', 'Brochure', 'Flyer', 'Banner'],
    idealIndustries: ['corporate', 'technology', 'finance', 'education', 'events'],
    idealAudiences: ['professionals'],
    idealGenomes: ['corporate', 'swiss', 'minimal', 'modern'],
    compatiblePatterns: ['split-hero', 'stat-row', 'centered-poster'],
    compositionPhilosophy: 'One horizontal band; optical sizing beats mathematical sizing.',
    hierarchyPhilosophy: 'Deliberately quiet — support, never a focal point.',
    typicalSections: ['logos', 'caption'],
    preferredComponents: ['logo'],
    premiumCues: ['restraint', 'precision'],
    thingsToAvoid: ['mismatched-logo-weights', 'clutter'],
    variationRules: ['logo-count', 'greyscale-or-color'],
    capabilities: ['logo', 'multi-column'],
    tags: ['logos', 'trust', 'block'],
    expansionRules: { emphasis: 'uniformity', tone: 'muted' },
  }),
  definePattern({
    id: 'corporate-footer', name: 'Corporate Footer', family: 'block',
    purpose: 'The closing band of legal, contact and brand detail.',
    communicationGoal: 'Close the design with completeness, not noise.',
    visualGoal: 'Compressed, orderly, quiet.',
    contentGoal: 'Brand mark, contact, legal line, socials.',
    marketingGoal: 'Reassure and provide the last mile of information.',
    idealDocuments: ['Brochure', 'Presentation', 'Flyer', 'Email Header', 'Pamphlet'],
    idealIndustries: ['corporate', 'legal', 'finance', 'medical', 'education'],
    idealAudiences: ['professionals'],
    idealGenomes: ['corporate', 'swiss', 'minimal', 'premium-medical'],
    compatiblePatterns: ['contact-section', 'qr-block', 'logo-strip'],
    compositionPhilosophy: 'Compressed band on a strict alignment.',
    hierarchyPhilosophy: 'The quietest region of the design.',
    typicalSections: ['brand', 'contact', 'legal', 'social'],
    preferredComponents: ['footer', 'contact', 'logo'],
    premiumCues: ['restraint', 'precision'],
    thingsToAvoid: ['clutter', 'shouty-footer'],
    variationRules: ['legal-length', 'social-or-not'],
    capabilities: ['contact', 'logo', 'list'],
    tags: ['footer', 'closing', 'block'],
    expansionRules: { emphasis: 'completeness', density: 'compact' },
  }),
];
